import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from 'ionic-angular';
import * as debug from 'debug';
import * as _ from 'lodash';
import { AbTestsProvider } from '../ab-tests/ab-tests';
import { ToolsProvider } from '../tools/tools';
import { EventEmitter } from '../../includes/eventEmitter';
import { UserProvider } from '../../providers/user/user';
import { CrudApiProvider } from '../../providers/crud-api/crud-api';
import { DecimalPipe } from '@angular/common';
import { ConfigProvider } from '../config/config';

@Injectable()
export class OnboardingProvider extends EventEmitter {
  log = debug('app:OnboardingProvider');

  userTemp;
  createUnauthedAccount = true;

  // set in startOnBoarding after some data load
  flows = {};

  _activeFlow;
  _flowData;
  _stage;
  _activeModal;
  _activeNavData;

  stageInfo = {
    index: 0,
    total: 0,
    padding: 0, // in case we need to add a substep for certain flows/paths
  };

  constructor(
    public http: HttpClient,
    public modalCtrl: ModalController,
    public ab: AbTestsProvider,
    public tools: ToolsProvider,
    public user: UserProvider,
    public crudApi: CrudApiProvider,
    public decimalPipe: DecimalPipe,
    public configs: ConfigProvider
  ) {
    super();
    window['_onboarding'] = this;
    this.tools.onboarding = this;
  }

  async startOnboarding(flow?, opts?: { skipSplash: boolean }) {
    if (flow !== 'flowSSO' && flow !== 'flowSkip') {
      if (flow === 'later') {
        opts = { skipSplash: true };
      }
      flow = 'flowSSO';
    }

    const userCount = await this.loadUserCount();

    // remove all listeners that are on a "once"
    this.onceListeners = {};

    this.flows = {
      // this is for an A/B test that sends some users straight to the feed
      flowSkip: {
        stages: [
          {
            page: 'HomePage',
            settings: {},
            unauthed: true,
          },
        ],
      },
      flow2: {
        stages: [
          {
            page: 'RegistrationSplashScreensPage',
            settings: {},
          },
          {
            page: 'RegistrationSignupPage',
            settings: {
              title: 'Join our community!',
              description: `Sign up and join ${this.decimalPipe.transform(
                userCount
              )} other drivers`,
              emailRegistrationFields: [
                'firstName',
                'lastName',
                'username',
                'email',
                'password',
                'dob',
                'zip',
              ],
            },
          },
          {
            page: 'RegistrationTruckingExperiencePage',
            settings: {
              title: 'What is your trucking experience',
            },
          },
          {
            page: 'RegistrationDrivingExperiencePage',
            settings: {
              title: 'What is your trucking experience',
            },
          },
          // {
          //   page: 'RegistrationWhatBringsYouPage',
          //   settings: {
          //     title: 'What brings you to CDLLife?',
          //   },
          // },
        ],
      },
      laterLogin: {
        stages: [
          {
            page: 'RegistrationLoginPage',
            settings: {},
          },
        ],
      },
      later: {
        stages: [
          {
            page: 'RegistrationSignupPage',
            settings: {
              title: 'Join our community!',
              description: `Sign up and join ${this.decimalPipe.transform(
                userCount
              )} other drivers`,
              emailRegistrationFields: [
                'firstName',
                'lastName',
                'username',
                'email',
                'password',
                'dob',
                'zip',
              ],
            },
          },
          {
            page: 'RegistrationTruckingExperiencePage',
            settings: {
              title: 'What is your trucking experience',
            },
          },
          {
            page: 'RegistrationDrivingExperiencePage',
            settings: {
              title: 'What is your trucking experience',
            },
          },
          // {
          //   page: 'RegistrationWhatBringsYouPage',
          //   settings: {
          //     title: 'What brings you to CDLLife?',
          //   },
          // },
        ],
      },
      // flow4: {
      flowSSO: {
        stages: [
          {
            page: 'RegistrationSplashScreensPage',
            settings: {
              title: 'Tell us about yourself',
              description: 'This will only take a minute!',
            },
          },
          {
            page: 'EntryPointsPage',
            settings: {
              title: 'Get Started',
              description:
                'Unlock the full potential of the CDLLife community and enjoy exclusive features!',
            },
          },
          {
            //todo: update title and description to state email or phone depending on what user entered
            page: 'verificationPage',
            settings: {
              title: `Verify your account`,
              description: 'Enter a code sent to your email or phone',
            },
          },
          {
            page: 'SsoRequestEmailPage',
            settings: {
              title: `What's your email address?`,
              description:
                'The email entered below will be used as a back-up option to log in to your CDLLife accounts',
            },
          },
          {
            page: 'OnboardingStep1Page',
            settings: {
              title: 'Tell us about yourself',
              description: 'This will only take a minute!',
            },
          },
          {
            page: 'OnboardingStep2Page',
            settings: {
              title: 'Tell us about yourself',
              description: 'This will only take a minute!',
            },
          },
          {
            page: 'OnboardingStep3aPage',
            settings: {
              title: 'Tell us about yourself',
              description: 'This will only take a minute!',
            },
          },
          {
            page: 'OnboardingStep3bPage',
            settings: {
              title: 'Tell us about yourself',
              description: 'This will only take a minute!',
            },
          },
        ],
      },
    };

    flow =
      flow ||
      this.ab.choose(
        'onboardingFlow',
        Object.keys(this.flows).filter(d => !/later/i.test(d))
      );

    if (
      this.tools.isWeb &&
      !this.tools.isMobileWeb &&
      !this.user.info?._id &&
      this.configs.get('auth-web-ua-flow-skip') &&
      !_.get(opts, 'skipSplash')
    ) {
      // on desktop web, always start unauthed
      flow = 'flowSkip';
    }

    if (flow === 'flowSkip') {
      return Promise.resolve();
    }

    this.log('flow === ', flow);

    if (_.get(opts, 'skipSplash')) {
      this.log('skipSplash', this.flows[flow].stages);
      const newStages = _.filter(
        this.flows[flow].stages,
        stage => stage.page !== 'RegistrationSplashScreensPage'
      );
      this.flows[flow].stages = newStages;
    }

    this._activeFlow = this.flows[flow];

    this.log('skipSplash', this._activeFlow.stages);

    const firstStage = (this._stage = this._activeFlow.stages[0]);

    this._flowData = {};

    return new Promise<void>((resolve, reject) => {
      this.setStageAsActive(firstStage);

      const modal = (this._activeModal = this.modalCtrl.create(
        firstStage.page,
        this._activeNavData,
        {
          enableBackdropDismiss: false,
        }
      ));

      modal.onDidDismiss(() => {
        resolve();
      });

      this.log('modal', modal);
      modal.present();

      const i = setInterval(() => {
        if (this.getNav()) {
          clearInterval(i);
          this.getNav().viewWillEnter.subscribe(view => {
            try {
              this.log('view will enter', view);
              const stage = view.data.stage;
              this.log('stage', stage);

              if (view.id === 'RegistrationSignupEmailPage') {
                this.stageInfo.padding = 1;
              } else if (view.id === 'RegistrationSignupPage') {
                this.stageInfo.padding = 0;
              }

              if (stage) {
                this.setStageAsActive(stage);
              }
            } catch (e) {}
          });
          this.getNav().viewDidEnter.subscribe(view => {
            setTimeout(() => {
              try {
                this.log('view did enter', view);
                const stage = view.data.stage;
                if (!stage) {
                  // they went to a view not in the flow, like an offshoot
                  // (e.g.forgot pw)
                  const views = this.getNav()._views;
                  let lastStage;
                  this.log('didnt find - views', views);
                  _.each(views, v => {
                    const stage = v.data.stage;
                    if (stage) lastStage = stage;
                  });
                  this.log('last stage', lastStage);
                  if (lastStage) {
                    this.setStageAsActive(lastStage);
                  }
                }
              } catch (e) {}
            });
          });
        }
      }, 50);
    });
  }

  loadUserCount() {
    const fallBackUserValue = 327006;
    return new Promise((resolve, reject) => {
      this.crudApi
        .get('/api/users/count')
        .then(res => {
          return resolve(_.get(res, 'data') || fallBackUserValue);
        })
        .catch(e => {
          return resolve(fallBackUserValue);
        });
    });
  }

  private getFlow(s?, u?) {
    if (s) {
      return _.get(this._activeFlow, s, u || undefined);
    } else {
      return this._activeFlow;
    }
  }

  getFlowData(s?, u?) {
    if (s) {
      return _.get(this._flowData, s, u || undefined);
    } else {
      return this._flowData;
    }
  }

  getNavData(s?, u?) {
    if (s) {
      return _.get(this._activeNavData, s, u || undefined);
    } else {
      return this._activeNavData;
    }
  }

  private setNavData(k, v) {
    return _.set(this._activeNavData, k, v);
  }

  mergeNavData(key, v) {
    const current = this.getNavData(key, {});
    this.setNavData(key, current);
    return _.merge(current, v);
  }

  next(skip = 0, goTo?) {
    this.log('go next from', this._stage);
    const stages = this.getFlow('stages', []),
      idx = goTo || goTo === 0 ? goTo : _.findIndex(stages, this._stage),
      nextStage = this.getFlow(`stages.${idx + 1 + skip}`);

    this.log('next page', nextStage);
    if (nextStage) {
      this._stage = nextStage;
      this.log('nextStage', nextStage);

      const nav = this.getNav();
      this.setStageAsActive(nextStage);
      this.tools.logAnalyticsEvent(
        'trackCustom',
        'onboarding-' + nextStage.page
      );
      nav.push(nextStage.page, this._activeNavData);
    } else {
      this.log('idx', idx);
      this.log(this._stage);
      this.log(stages);
      this.complete();
    }
  }

  skip() {
    this.log('skip onboarding');
    this.tools.logAnalyticsEvent('trackCustom', 'onboarding-skip');
    this.close();
  }

  complete() {
    this.log('complete');
    this.tools.logAnalyticsEvent('trackCustom', 'onboarding-complete');
    this.emit('complete');
    this.close(true);
  }

  close(refresh = false) {
    this.log('close');
    this.tools.logAnalyticsEvent('trackCustom', 'onboarding-close');
    this.emit('close');

    try {
      this._activeModal.overlay._nav._views.forEach(v => v.dismiss());
    } catch (e) {}

    if (refresh) {
      window.location.reload();
    }
  }

  // Internal fns -----------

  private setStageAsActive(stage) {
    const stages = this.getFlow('stages', []),
      idx = _.findIndex(stages, stage);

    this.stageInfo.index = idx + this.stageInfo.padding;
    this.stageInfo.total = _.size(stages) + this.stageInfo.padding;
    this._stage = stage;
    this._activeNavData = {
      settings: stage.settings,
      flowData: this._flowData,
      stage: stage,
    };
  }

  private getNav() {
    try {
      return this._activeModal.overlay._nav;
    } catch (e) {}
  }
}



// WEBPACK FOOTER //
// ./src/providers/onboarding/onboarding.ts